import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Phone, Calendar, ShoppingCart, Plus, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { GALLERY_IMAGES, COMPANY_INFO } from '../data/siteData';
import { useServices } from '../context/ServicesContext';
import { useCart } from '../context/CartContext';
import { Toaster, toast } from 'sonner';
import { API } from '../config/api';
import axios from 'axios';

export default function ServiceDetail() {
  const { slug } = useParams();
  const { services, loading } = useServices();
  const service = services.find((s) => s.slug === slug);
  const { addItem, removeItem, items } = useCart();
  const navigate = useNavigate();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [dynamicPhotos, setDynamicPhotos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/gallery-photos`).then((res) => {
      setDynamicPhotos(res.data);
    }).catch(() => {});
  }, []);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center" data-testid="service-loading" />;
  }

  if (!service) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="service-not-found">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">Service Not Found</h1>
          <Link to="/" className="text-sky-700 font-medium hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  // Collect all photos for this service: main image + matching gallery images + dynamic uploads
  const dynamicMapped = dynamicPhotos
    .filter((p) => p.category === service?.name)
    .map((p) => ({ url: `${API}/photos/serve/${p.id}`, caption: p.caption || p.original_filename }));
  const servicePhotos = [
    { url: service.image, caption: service.name },
    ...GALLERY_IMAGES.filter((img) =>
      img.category.toLowerCase().includes(service.name.toLowerCase().split(' ')[0])
    ),
    ...dynamicMapped,
  ];
  // Deduplicate by URL
  const uniquePhotos = servicePhotos.filter((p, i, arr) => arr.findIndex((q) => q.url === p.url) === i);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const prevPhoto = () => setLightboxIndex((i) => (i - 1 + uniquePhotos.length) % uniquePhotos.length);
  const nextPhoto = () => setLightboxIndex((i) => (i + 1) % uniquePhotos.length);

  const handleToggleCart = (p) => {
    const priceNum = parseFloat(p.price.replace(/[^0-9.]/g, ''));
    const cartIndex = items.findIndex((i) => i.service === service.name && i.option === p.label);
    if (cartIndex >= 0) {
      removeItem(cartIndex);
      toast('Removed from cart', { icon: '✕' });
    } else {
      addItem({ service: service.name, option: p.label, price: priceNum });
      toast.success(`${service.name} added to cart`);
    }
  };

  return (
    <div data-testid={`service-page-${slug}`}>
      {/* Header */}
      <section className="bg-sky-50 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <a
            href="/#services"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = '/#services';
            }}
            data-testid="service-back-link"
            className="flex items-center gap-2 text-sky-600 text-sm mb-4 hover:text-sky-800 transition-colors w-fit"
          >
            <ArrowLeft className="w-4 h-4" /> Back to All Services
          </a>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-sky-700 rounded-xl flex items-center justify-center">
              <service.icon className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-black text-slate-900">
              {service.name}
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main content */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
                About This Service
              </h2>
              <p className="text-slate-600 text-base leading-relaxed mb-8">{service.fullDesc}</p>

              {/* Features */}
              <div className="mb-10">
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">What's Included</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {service.features.map((f) => (
                    <div key={f} className="flex items-center gap-3 bg-sky-50 rounded-xl px-4 py-3">
                      <div className="w-6 h-6 bg-sky-700 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                      <span className="text-slate-700 text-sm font-medium">{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Photo examples - clickable with lightbox */}
              <div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">Photo Examples</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="service-photos">
                  {uniquePhotos.map((photo, i) => (
                    <button
                      key={i}
                      onClick={() => openLightbox(i)}
                      className="gallery-item rounded-2xl overflow-hidden h-56 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-400 border border-sky-100"
                      data-testid={`service-photo-${i}`}
                    >
                      <img src={photo.url} alt={photo.caption || `${service.name} example ${i + 1}`} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar - Pricing & CTA */}
            <div className="lg:col-span-1">
              <div className="sticky top-32">
                {/* Pricing card */}
                <div className="bg-white rounded-2xl border border-sky-100 shadow-lg p-6 mb-6" data-testid="service-pricing-card">
                  <Toaster position="top-right" richColors />
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-1">Select Appropriate Service</h3>
                  <p className="text-slate-400 text-xs mb-5">Additional fees may be placed after in-person property assessment.</p>
                  <div className="space-y-3">
                    {service.pricing.map((p) => {
                      const inCart = items.some((i) => i.service === service.name && i.option === p.label);
                      return (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => handleToggleCart(p)}
                          data-testid={`add-to-cart-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`w-full flex items-center gap-2 rounded-xl px-4 py-3 text-left transition-all ${
                            inCart
                              ? 'bg-green-50 border-2 border-green-200'
                              : 'bg-sky-50 border-2 border-transparent hover:border-sky-300 hover:shadow-sm cursor-pointer'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <span className="text-slate-700 text-sm block">{p.label}</span>
                          </div>
                          <span className="font-heading font-bold text-sky-700 whitespace-nowrap">{p.price}</span>
                          <div className={`flex-shrink-0 p-1 rounded-lg ${inCart ? 'bg-green-100 text-green-600' : 'bg-sky-200/50 text-sky-700'}`}>
                            {inCart ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {service.pricingNote && (
                    <p className="text-slate-500 text-xs italic mt-3">{service.pricingNote}</p>
                  )}

                  {/* Something Unique option */}
                  <Link
                    to="/contact"
                    data-testid="something-unique-link"
                    className="w-full flex items-center gap-2 rounded-xl px-4 py-3 mt-3 bg-sky-700/5 border-2 border-dashed border-sky-200 hover:border-sky-400 hover:bg-sky-50 transition-all text-left"
                  >
                    <div className="flex-1">
                      <span className="text-sky-700 text-sm font-medium block">Something Unique?</span>
                      <span className="text-slate-400 text-xs">Get a call back</span>
                    </div>
                    <Phone className="w-4 h-4 text-sky-500" />
                  </Link>

                  {items.length > 0 && (
                    <>
                      <button
                        onClick={() => navigate('/book')}
                        data-testid="go-to-cart-btn"
                        className="w-full mt-4 bg-sky-700 text-white font-semibold rounded-full py-2.5 text-sm hover:bg-sky-600 transition-colors flex items-center justify-center gap-2"
                      >
                        <ShoppingCart className="w-4 h-4" /> View Cart ({items.length})
                      </button>
                      <Link
                        to="/"
                        onClick={() => setTimeout(() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' }), 300)}
                        data-testid="add-service-btn"
                        className="w-full mt-2 border-2 border-sky-200 text-sky-700 font-semibold rounded-full py-2.5 text-sm hover:bg-sky-50 transition-colors flex items-center justify-center gap-2"
                      >
                        <Plus className="w-4 h-4" /> Add Service
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          data-testid="photo-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-10"
            data-testid="lightbox-close"
          >
            <X className="w-7 h-7" />
          </button>

          {uniquePhotos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                className="absolute left-4 text-white/70 hover:text-white p-2 bg-black/30 rounded-full z-10"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                className="absolute right-4 text-white/70 hover:text-white p-2 bg-black/30 rounded-full z-10"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
            </>
          )}

          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl max-h-[85vh] px-4">
            <img
              src={uniquePhotos[lightboxIndex]?.url}
              alt={uniquePhotos[lightboxIndex]?.caption || ''}
              className="max-w-full max-h-[80vh] object-contain rounded-xl mx-auto"
            />
            {uniquePhotos[lightboxIndex]?.caption && (
              <p className="text-white/70 text-sm text-center mt-3">{uniquePhotos[lightboxIndex].caption}</p>
            )}
            {uniquePhotos.length > 1 && (
              <p className="text-white/40 text-xs text-center mt-1">{lightboxIndex + 1} / {uniquePhotos.length}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
